import React, { memo, useCallback, useState } from "react";
import { Box, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { bankExtractionCopy } from "@investorRequest/constants/bankExtractionCopy";
import { visuallyHiddenSx } from "@investorRequest/components/BankExtractionFieldChrome";

interface Props {
  fieldName: string;
  value: string;
  candidates: string[];
  label?: string;
  onChange: (value: string) => void;
  onAcknowledge: () => void;
  onCustomValue?: (value: string) => void;
}

const BankExtractionAmbiguousField = ({
  fieldName,
  value,
  candidates,
  label,
  onChange,
  onAcknowledge,
  onCustomValue,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const announceId = `extraction-announce-${fieldName}`;
  const showAmber = !opened;

  const handleOpen = useCallback(() => {
    setOpened(true);
    onAcknowledge();
  }, [onAcknowledge]);

  const handleChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const next = String(event.target.value);

      onChange(next);

      if (!candidates.includes(next)) {
        onCustomValue?.(next);
      }
    },
    [candidates, onChange, onCustomValue]
  );

  return (
    <Box data-testid="bank-extraction-ambiguous-field" position="relative">
      <Select
        fullWidth
        value={value}
        displayEmpty
        inputProps={{
          "aria-invalid": showAmber,
          "aria-label": label,
          autoComplete: "off",
        }}
        onOpen={handleOpen}
        onChange={handleChange}
        sx={{
          ...(showAmber
            ? {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "warning.main",
                  borderWidth: 1,
                },
              }
            : {}),
        }}
        endAdornment={
          showAmber ? (
            <Tooltip title={bankExtractionCopy.ambiguousTooltip} arrow>
              <Box component="span" aria-hidden sx={{ color: "warning.main", pr: 2 }}>
                ▲
              </Box>
            </Tooltip>
          ) : undefined
        }
      >
        {candidates.map((candidate) => (
          <MenuItem key={candidate} value={candidate}>
            {candidate}
          </MenuItem>
        ))}
      </Select>
      <Typography id={announceId} aria-live="polite" sx={visuallyHiddenSx}>
        {opened ? bankExtractionCopy.ambiguousAnnounce : ""}
      </Typography>
    </Box>
  );
};

export default memo(BankExtractionAmbiguousField);
